/**
 * TicketBell — real-time notification for the currently logged-in staff
 * member when a ticket is assigned to them. Polls `/tickets/assigned/poll`
 * every 10s. Rings `new task.mp3` when either (a) the count of open
 * assigned tickets goes up, or (b) the latest.assigned_at is newer than
 * the last one we saw. Mute toggle persists in `localStorage`
 * (`rescigo.ticket_bell.muted`). Click → navigates to
 * `/crm/tickets` (Assigned Tasks tab).
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardText, BellSlash, Bell } from "@phosphor-icons/react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const POLL_MS = 10000;
const TASK_MP3 =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_rescigo-master-panel/artifacts/h7rxl6dz_new%20task.mp3";
const MUTE_KEY = "rescigo.ticket_bell.muted";

function getToken() {
  return localStorage.getItem("rescigo_token") || "";
}

export default function TicketBell() {
  const [count, setCount] = useState(0);
  const [muted, setMuted] = useState(() => localStorage.getItem(MUTE_KEY) === "1");
  const [notifPerm, setNotifPerm] = useState(() =>
    typeof window !== "undefined" && "Notification" in window
      ? Notification.permission
      : "unsupported"
  );
  const lastCountRef = useRef(0);
  const lastAssignedAtRef = useRef(null);
  const firstTickRef = useRef(true);
  const audioRef = useRef(null);
  const navigate = useNavigate();

  // Iter 113 — one-tap enable button; browsers require the request to
  // originate from a user gesture so it can't be auto-triggered.
  const requestNotifPermission = async () => {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    try {
      const perm = await Notification.requestPermission();
      setNotifPerm(perm);
    } catch { /* silently ignore — Firefox in private throws */ }
  };

  useEffect(() => {
    audioRef.current = new Audio(TASK_MP3);
    audioRef.current.preload = "auto";
    audioRef.current.volume = 0.7;
  }, []);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await fetch(`${API}/tickets/assigned/poll`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (!r.ok) return;
        const d = await r.json();
        if (cancelled) return;

        const n = d.open_assigned || 0;
        const newestAt = d.latest?.assigned_at || d.latest?.created_at || null;

        // Ring rules — only ring on state CHANGE, not on first mount
        // (avoids double-ringing when the page loads with existing work).
        const isFirst = firstTickRef.current;
        firstTickRef.current = false;

        const countIncreased = n > lastCountRef.current;
        const newestChanged =
          newestAt && newestAt !== lastAssignedAtRef.current &&
          lastAssignedAtRef.current !== null;

        if (!isFirst && (countIncreased || newestChanged) && !muted) {
          try { audioRef.current?.play().catch(() => {}); } catch { /* autoplay blocked */ }
          const t = d.latest;
          if (t) {
            toast.success(
              `New ticket assigned · ${t.ticket_no}${t.customer_name ? " · " + t.customer_name : ""}`,
              {
                description: t.issue?.slice(0, 90) || "Tap to review",
                action: {
                  label: "Open",
                  onClick: () => navigate("/crm/tickets?tab=assigned"),
                },
                duration: 5000,
              }
            );
            // Iter 113 — fire a native browser notification so the assignee
            // sees the ping even when the tab is in the background (or on
            // another virtual desktop). Silently no-ops if permission has
            // not been granted yet — user grants via the bell mute button.
            try {
              if (typeof window !== "undefined" &&
                  "Notification" in window &&
                  Notification.permission === "granted" &&
                  document.hidden) {
                const n = new Notification(
                  `New ticket · ${t.ticket_no}`,
                  {
                    body: `${t.customer_name || "Assigned to you"} · ${
                      (t.issue || "").slice(0, 120) || "Tap to review"}`,
                    icon: "/favicon.ico",
                    tag: `ticket-${t.ticket_no}`,
                    // Reuse a system stack so 5 quick assigns don't spam
                    // 5 popups — the newest one supersedes.
                    renotify: false,
                  }
                );
                n.onclick = () => {
                  window.focus();
                  navigate("/crm/tickets?tab=assigned");
                  n.close();
                };
              }
            } catch { /* Notification constructor unsupported in some browsers */ }
          }
        }

        setCount(n);
        lastCountRef.current = n;
        if (newestAt) lastAssignedAtRef.current = newestAt;
      } catch { /* silent — topbar keeps rendering */ }
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [muted, navigate]);

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    localStorage.setItem(MUTE_KEY, next ? "1" : "0");
  };

  return (
    <div className="flex items-center gap-1" data-testid="topbar-ticket-bell">
      <button
        onClick={() => navigate("/crm/tickets?tab=assigned")}
        data-testid="topbar-ticket-bell-link"
        title={count > 0 ? `${count} open assigned ticket(s)` : "Assigned tickets"}
        className="relative p-2 rounded-md text-[#5C5A55] hover:text-[#2D5A27] hover:bg-[#F0EFEA] transition-colors"
      >
        <ClipboardText size={20} weight={count > 0 ? "fill" : "regular"}/>
        {count > 0 && (
          <span
            data-testid="topbar-ticket-bell-count"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#7A2E18] text-white text-[10px] font-semibold flex items-center justify-center ring-2 ring-white animate-pulse"
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>
      <button
        onClick={toggleMute}
        data-testid="topbar-ticket-bell-mute"
        title={muted ? "Task sound muted" : "Task sound on"}
        className="p-2 rounded-md text-[#8A8780] hover:text-[#1A1A1A] hover:bg-[#F0EFEA] transition-colors"
      >
        {muted ? <BellSlash size={16}/> : <Bell size={16}/>}
      </button>
      {notifPerm === "default" && (
        <button
          onClick={requestNotifPermission}
          data-testid="topbar-ticket-bell-enable-push"
          title="Get a browser popup when a ticket is assigned to you"
          className="text-[10px] uppercase tracking-[0.14em] font-semibold px-2 h-[26px] rounded-md bg-[#F0F7EE] text-[#1A3317] border border-[#B5D5AC] hover:bg-[#DDECD9]"
        >
          Enable Push
        </button>
      )}
      {notifPerm === "denied" && (
        <span
          data-testid="topbar-ticket-bell-push-denied"
          title="Browser notifications blocked — enable in site settings"
          className="text-[10px] uppercase tracking-[0.14em] font-semibold px-2 h-[26px] rounded-md bg-[#F0EFEA] text-[#8A8780] border border-[#E5E2D9] inline-flex items-center"
        >
          Push off
        </span>
      )}
    </div>
  );
}
